import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { MusicNotesSimple, SignOut, Coins, Bell, ArrowsClockwise } from "@phosphor-icons/react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [alerts, setAlerts] = useState(null);

  // Poll admin alerts every 20s when user is admin
  useEffect(() => {
    if (!user?.is_admin) return;
    const fetchAlerts = () => api.get("/admin/alerts").then(r => setAlerts(r.data)).catch(() => {});
    fetchAlerts();
    const t = setInterval(fetchAlerts, 20000);
    return () => clearInterval(t);
  }, [user]);

  const reviewCount = alerts?.review_count || 0;
  const pendingCount = alerts?.pending_count || 0;

  return (
    <header className="sticky top-0 z-40 backdrop-blur-2xl bg-black/60 border-b border-white/5">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-brand">
          <div className="w-9 h-9 rounded-full bg-[#FFB800] flex items-center justify-center group-hover:scale-105 transition-transform">
            <MusicNotesSimple weight="fill" size={20} className="text-black" />
          </div>
          <span className="font-display font-bold text-xl tracking-tight">Tu MusiKa</span>
        </Link>

        <nav className="flex items-center gap-2">
          <Link
            to="/concurso"
            className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-full bg-[#FFB800]/10 hover:bg-[#FFB800]/20 text-[#FFB800] font-semibold transition-colors"
            data-testid="nav-contest"
          >
            🏆 Concurso
          </Link>
          {user ? (
            <>
              {user.is_admin && (
                <Link
                  to="/admin"
                  className="relative px-3 py-1.5 text-sm rounded-full bg-[#FF4B00]/10 hover:bg-[#FF4B00]/20 text-[#FF4B00] font-semibold transition-colors flex items-center gap-1.5"
                  data-testid="nav-admin"
                >
                  <Bell size={14} weight={reviewCount > 0 ? "fill" : "regular"} />
                  Admin
                  {reviewCount > 0 && (
                    <span
                      data-testid="nav-admin-badge"
                      className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF4B00] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-black animate-pulse"
                    >
                      {reviewCount > 9 ? '9+' : reviewCount}
                    </span>
                  )}
                </Link>
              )}

              {!user.is_admin && (
                <Link
                  to="/payment"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full bg-[#FFB800]/10 hover:bg-[#FFB800]/20 text-[#FFB800] font-semibold transition-colors"
                  data-testid="nav-credits"
                >
                  <Coins size={14} weight="fill" />
                  <span>{user.song_credits || 0}</span>
                  <span className="hidden md:inline text-white/50 font-normal text-xs ml-1">crédito{user.song_credits === 1 ? '' : 's'}</span>
                </Link>
              )}

              {!user.is_admin && (
                <Link
                  to="/cambios-extra"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full bg-[#B4F34A]/10 hover:bg-[#B4F34A]/20 text-[#B4F34A] font-semibold transition-colors"
                  data-testid="nav-regen-credits"
                  title="Cambios extra disponibles"
                >
                  <ArrowsClockwise size={14} weight="bold" />
                  <span>{user.regen_credits || 0}</span>
                  <span className="hidden md:inline text-white/50 font-normal text-xs ml-1">cambio{user.regen_credits === 1 ? '' : 's'}</span>
                </Link>
              )}

              <Link
                to="/library"
                className="hidden sm:inline-block px-3 py-1.5 text-sm rounded-full hover:bg-white/5 transition-colors text-white/80"
                data-testid="nav-library"
              >
                Mis canciones
              </Link>
              {!user.is_admin && (
                <Link
                  to={user.song_credits > 0 ? "/create" : "/payment"}
                  className="px-4 py-2 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black font-semibold text-sm transition-all active:scale-95"
                  data-testid="nav-create"
                >
                  {user.song_credits > 0 ? "Crear" : "Comprar"}
                </Link>
              )}
              <button
                onClick={() => { logout(); nav("/"); }}
                className="p-2 rounded-full hover:bg-white/5 text-white/70"
                aria-label="Cerrar sesión"
                data-testid="nav-logout"
              >
                <SignOut size={18} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="px-3 py-1.5 text-sm text-white/80 hover:text-white" data-testid="nav-login">
                Iniciar sesión
              </Link>
              <Link
                to="/register"
                className="px-4 py-2 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black font-semibold text-sm transition-all active:scale-95"
                data-testid="nav-register"
              >
                Crear cuenta
              </Link>
            </>
          )}
        </nav>
      </div>

      {/* Mobile-only secondary bar with scrollable pills for the hidden nav items */}
      {user && (
        <div className="sm:hidden border-t border-white/5 bg-black/40 px-3 py-2 overflow-x-auto no-scrollbar" data-testid="nav-mobile-secondary">
          <div className="flex items-center gap-2 min-w-max">
            <Link
              to="/library"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 text-white/80 text-xs font-bold whitespace-nowrap"
              data-testid="nav-mobile-library"
            >
              🎵 Mis canciones
            </Link>
            {!user.is_admin && (
              <Link
                to="/payment"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FFB800]/10 text-[#FFB800] text-xs font-bold whitespace-nowrap"
                data-testid="nav-mobile-credits"
              >
                <Coins size={12} weight="fill" />
                {user.song_credits || 0} crédito{user.song_credits === 1 ? '' : 's'}
              </Link>
            )}
            {!user.is_admin && (
              <Link
                to="/cambios-extra"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#B4F34A]/10 text-[#B4F34A] text-xs font-bold whitespace-nowrap"
                data-testid="nav-mobile-regen-credits"
              >
                <ArrowsClockwise size={12} weight="bold" />
                {user.regen_credits || 0} cambio{user.regen_credits === 1 ? '' : 's'}
              </Link>
            )}
            <Link
              to="/concurso"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FFB800]/10 text-[#FFB800] text-xs font-bold whitespace-nowrap"
              data-testid="nav-mobile-contest"
            >
              🏆 Concurso
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
